<template>
  <div class="min-h-screen bg-gray-50">
    <Header />
    <main class="p-6">
      <div class="max-w-5xl mx-auto">
        <div class="flex items-center justify-between mb-6">
          <h1 class="text-2xl font-bold">Tenants</h1>
          <button class="btn btn-primary" @click="showCreate = true">Create Tenant</button>
        </div>

        <ErrorMessage v-if="error" :message="error" title="Error" class="mb-8" />

        <div class="card mb-8">
          <div class="card-body p-0">
            <table class="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tenant ID</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in tenants" :key="t.tenantId">
                  <td class="font-medium">{{ t.name }}</td>
                  <td class="font-mono text-sm">{{ t.tenantId }}</td>
                  <td>
                    <span :class="t.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" class="px-2 py-1 text-xs rounded-full">
                      {{ t.isActive ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td>
                    <button class="btn btn-danger btn-sm" @click="deactivate(t.tenantId)" :disabled="!t.isActive">Delete</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Create Modal -->
      <div v-if="showCreate" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div class="card-header">
            <h2 class="text-xl font-semibold">Create Tenant</h2>
            <button class="text-gray-400 hover:text-gray-600" @click="showCreate=false">✕</button>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="form-label">Name</label>
              <input v-model="createForm.name" class="form-input" placeholder="Tenant name" />
            </div>
            <div class="form-group">
              <label class="form-label">Description</label>
              <input v-model="createForm.description" class="form-input" placeholder="Optional description" />
            </div>
            <div class="flex justify-end gap-2 mt-4">
              <button class="btn btn-secondary" @click="showCreate=false">Cancel</button>
              <button class="btn btn-primary" @click="create">Create</button>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import Header from '@/components/common/Header.vue'
import ErrorMessage from '@/components/common/ErrorMessage.vue'
import { useAdminStore } from '@/stores/admin'
import { adminService } from '@/services/admin'

const adminStore = useAdminStore()
const tenants = computed(() => adminStore.tenants)
const error = ref('')
const showCreate = ref(false)
const createForm = ref({ name: '', description: '' })

const refresh = async () => {
  try {
    error.value = ''
    await adminStore.fetchTenants()
  } catch (e: any) {
    error.value = e?.message || 'Failed to load tenants'
  }
}

const create = async () => {
  try {
    await adminStore.createTenant({ ...createForm.value })
    showCreate.value = false
    createForm.value = { name: '', description: '' }
  } catch (e: any) {
    error.value = e?.message || 'Failed to create tenant'
  }
}

const deactivate = async (tenantId: string) => {
  if (!confirm('Deactivate this tenant?')) return
  try {
    await adminService.deactivateTenant(tenantId)
    await refresh()
  } catch (e: any) {
    error.value = e?.message || 'Failed to deactivate tenant'
  }
}

onMounted(refresh)
</script>
